import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import LandingPage from './pages/LandingPage';
import AIVisibilityLanding from './pages/AIVisibilityLanding';
import Dashboard from './pages/Dashboard';
import NotFound from './pages/NotFound';
import AuthPage from './pages/AuthPage';
import ProjectSettings from './pages/ProjectSettings';
import ComparativeReportPage from './pages/ComparativeReportPage';
import LearnMore from './pages/LearnMore';
import Pricing from './pages/Pricing';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookieBanner from './components/CookieBanner';
import CookiePolicy from './pages/CookiePolicy';
import TermsOfService from './pages/TermsOfService';
import ClientIntake from './pages/ClientIntake';
import WebsiteIntelligenceReport from './pages/WebsiteIntelligenceReport';
import { Toaster } from './components/ui/sonner';
import AIAssistant from './components/AIAssistant';
import PaymentSignup from './pages/PaymentSignup';
import PaymentPassword from './pages/PaymentPassword';
import UserSettings from './pages/UserSettings';
import VeteranAdmin from './pages/VeteranAdmin';
import { AdminLogin, AdminLayout, AdminOverview, AdminUsers } from './pages/Admin';
import AdminBlogs from './pages/admin/AdminBlogs';
import AdminBlogEditor from './pages/admin/AdminBlogEditor';
import AdminBlogAuthors from './pages/admin/AdminBlogAuthors';
import AdminBlogCategories from './pages/admin/AdminBlogCategories';
import AdminBlogTags from './pages/admin/AdminBlogTags';
import AdminBlogUploads from './pages/admin/AdminBlogUploads';
import BlogPostPage from './pages/BlogPostPage';
import BlogListingPage from './pages/BlogListingPage';
import SearchAdviserDashboard from './pages/SearchAdviserDashboard';
import AccountAccess from './components/AccountAccess';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const cleanBaseUrl = BACKEND_URL.replace(/\/api\/?$/, '').replace(/\/$/, '');
const API = `${cleanBaseUrl}/api`;

// Set up axios defaults
axios.defaults.baseURL = API;

function PublicExtras() {
  const { pathname } = useLocation();
  return pathname.startsWith('/admin') ? null : <><AIAssistant /><CookieBanner /></>;
}

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || 'null'));
  const [accessReady, setAccessReady] = useState(false);
  const [accessError, setAccessError] = useState(false);
  const [accessRetry, setAccessRetry] = useState(0);

  // Ensure axios has the Authorization header immediately on first render
  // This prevents child components from firing requests before the header
  // is set (which can cause a 401 and show the "Failed to load projects" toast)
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
  useEffect(() => {
    let cancelled = false;
    setAccessReady(false);
    setAccessError(false);
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.get('/auth/me')
        .then(res => {
          if (cancelled) return;
          setUser(res.data.tenant);
          localStorage.setItem('user', JSON.stringify(res.data.tenant));
          setAccessReady(true);
        })
        .catch(() => { if (!cancelled) setAccessError(true); });
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
    return () => { cancelled = true; };
  }, [token, accessRetry]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    let pending = false;
    const applyStatus = (status) => {
      if (cancelled) return;
      setUser(previous => {
        if (!previous || previous.account_status === status) return previous;
        const next = { ...previous, account_status: status };
        localStorage.setItem('user', JSON.stringify(next));
        return next;
      });
    };
    const refresh = async () => {
      if (pending) return;
      pending = true;
      try {
        const { data } = await axios.get('/auth/status');
        applyStatus(data.account_status);
      } catch { /* Keep the last verified state during a temporary network failure. */ }
      finally { pending = false; }
    };
    const interceptor = axios.interceptors.response.use(response => response, error => {
      if (error.response?.status === 403 && error.response?.data?.detail === 'Your account is paused. Please contact support.') {
        applyStatus('paused');
      }
      return Promise.reject(error);
    });
    const timer = window.setInterval(refresh, 15000);
    window.addEventListener('focus', refresh);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
      axios.interceptors.response.eject(interceptor);
    };
  }, [token]);

  const handleLogin = (token, user) => {
    setToken(token);
    setUser(user);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
  };

  const handleProfileUpdate = (updatedUser) => {
    const nextUser = { ...user, ...updatedUser };
    setUser(nextUser);
    localStorage.setItem('user', JSON.stringify(nextUser));
  };

  return (
    <div className="App">
      <BrowserRouter>
        <AccountAccess user={user} token={token} ready={accessReady} error={accessError} onRetry={() => setAccessRetry(value => value + 1)} onLogout={handleLogout}>
        <Routes>
          <Route path="/" element={<AIVisibilityLanding />} />
          <Route path="/platform" element={<AIVisibilityLanding />} />
          <Route path="/scan" element={<ClientIntake />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminOverview />} />
            <Route path="blogs" element={<AdminBlogs />} />
            <Route path="blogs/new" element={<AdminBlogEditor />} />
            <Route path="blogs/edit/:id" element={<AdminBlogEditor />} />
            <Route path="blog-authors" element={<AdminBlogAuthors />} />
            <Route path="blog-categories" element={<AdminBlogCategories />} />
            <Route path="blog-tags" element={<AdminBlogTags />} />
            <Route path="blog-uploads" element={<AdminBlogUploads />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="veteran-verifications" element={<VeteranAdmin />} />
          </Route>
          <Route path="/blogs" element={<BlogListingPage />} />
          <Route path="/blog" element={<BlogListingPage />} />
          <Route path="/blogs/:slug" element={<BlogPostPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/blogs/:category/:slug" element={<BlogPostPage />} />
          <Route path="/pricing" element={<Pricing isAuthenticated={!!token} user={user} />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/payment-signup" element={<PaymentSignup />} />
          <Route path="/payment-password" element={<PaymentPassword onLogin={handleLogin} />} />
          <Route path="/client/report/:reportId" element={<WebsiteIntelligenceReport />} />
          <Route 
            path="/auth" 
            element={token ? <Navigate to="/dashboard" /> : <AuthPage onLogin={handleLogin} />} 
          />
          <Route 
            path="/login" 
            element={token ? <Navigate to="/dashboard" /> : <Navigate to="/auth" replace />} 
          />
          <Route 
            path="/dashboard" 
            element={token ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/user-settings" 
            element={token ? <UserSettings user={user} onProfileUpdate={handleProfileUpdate} /> : <Navigate to="/auth" />}
          />
          <Route 
            path="/projects/:projectId/settings" 
            element={token ? <ProjectSettings user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/projects/:projectId/report" 
            element={token ? <ComparativeReportPage user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/adviser" 
            element={token ? <SearchAdviserDashboard key={user?.id || 'signed-in'} user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          <Route path="/learn-more" element={<LearnMore />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <PublicExtras />
        </AccountAccess>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
